import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { onSocketEvent } from './socketClient';
import { playAlertChime } from './alertSound';

const ALERT_AUTO_DISMISS_MS = 12000;
const MAX_STACKED_ALERTS = 4;

/**
 * The frozen-pick-portal counterpart to useDeliveryRealtime.js — same shape,
 * copied on purpose (see that file's header comment inviting exactly this)
 * rather than growing AppShell or bolting a second concern onto the delivery
 * hook. Socket connection + OS push subscription are already bootstrapped
 * app-wide by useDeliveryRealtime (mounted once in AppShell for every
 * logged-in user), so this hook only needs to listen for its own event —
 * it does not open a second connection or request push permission again.
 */
export function useFrozenPickRealtime() {
    const { user } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const timersRef = useRef({});

    const dismissAlert = useCallback((id) => {
        setAlerts((prev) => prev.filter((a) => a.id !== id));
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
    }, []);

    const pushAlert = useCallback((alert) => {
        setAlerts((prev) => [...prev.slice(-(MAX_STACKED_ALERTS - 1)), alert]);
        timersRef.current[alert.id] = setTimeout(() => dismissAlert(alert.id), ALERT_AUTO_DISMISS_MS);
        playAlertChime();
    }, [dismissAlert]);

    useEffect(() => {
        if (!user) return undefined;

        const onNewJob = (payload) => pushAlert({
            id: `frozen-${payload.jobId}-${Date.now()}`,
            // headline: 'New invoice needs picking', 'Ready order amended —
            // back to Picking', … — absent from an older backend.
            title: payload.headline || 'Frozen items to pick',
            detail: `${payload.customerName || 'Invoice ' + payload.invoiceNo} — ${payload.lineCount} item(s)`
        });

        // onSocketEvent survives the socket being replaced on token rotation
        // and waits for it if useDeliveryRealtime hasn't opened it yet.
        return onSocketEvent('frozen-pick:new-job', onNewJob);
    }, [user, pushAlert]);

    return { alerts, dismissAlert };
}
